import type { CollectionMemberStatus, FirestoreCollectionMember } from '@creative-kit/shared';
import { coerceFirestoreDate, isValidDate } from './firestore-date';

export type CollectionStatusSection = 'pending' | 'scheduled' | 'used';

export const COLLECTION_STATUS_SECTION_LABELS: Record<CollectionStatusSection, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  used: 'Used',
};

const VISIBILITY_STORAGE_KEY = 'cq-collections-status-sections:v1';

const DEFAULT_VISIBILITY: Record<CollectionStatusSection, boolean> = {
  pending: true,
  scheduled: true,
  used: true,
};

export interface CollectionStatusDateGroup {
  dateIso: string;
  label: string;
  count: number;
  memberDocIds: string[];
}

export interface CollectionStatusBreakdown {
  pending: { count: number; memberDocIds: string[] };
  used: { count: number; memberDocIds: string[] };
  scheduledDates: CollectionStatusDateGroup[];
}

/** Calendar date as YYYY-MM-DD (local timezone — for date picker input). */
export function toCalendarIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Calendar ISO from a stored schedule timestamp (UTC calendar day). */
export function calendarIsoFromStoredDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatCollectionStatusDateLabel(dateIso: string): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function memberScheduledDateIso(member: FirestoreCollectionMember): string | null {
  if (member.status !== 'scheduled' || !member.scheduledDate) {
    return null;
  }
  const scheduled = coerceFirestoreDate(member.scheduledDate);
  if (!isValidDate(scheduled)) {
    return null;
  }
  return calendarIsoFromStoredDate(scheduled);
}

export function buildCollectionStatusBreakdown(
  members: Record<string, FirestoreCollectionMember>,
): CollectionStatusBreakdown {
  const pending: string[] = [];
  const used: string[] = [];
  const scheduledByDate = new Map<string, string[]>();

  Object.entries(members).forEach(([memberDocId, member]) => {
    if (member.status === 'used') {
      used.push(memberDocId);
      return;
    }
    if (member.status === 'scheduled') {
      const dateIso = memberScheduledDateIso(member);
      if (dateIso) {
        const list = scheduledByDate.get(dateIso) ?? [];
        list.push(memberDocId);
        scheduledByDate.set(dateIso, list);
      } else {
        pending.push(memberDocId);
      }
      return;
    }
    pending.push(memberDocId);
  });

  const scheduledDates = Array.from(scheduledByDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateIso, memberDocIds]) => ({
      dateIso,
      label: formatCollectionStatusDateLabel(dateIso),
      count: memberDocIds.length,
      memberDocIds,
    }));

  return {
    pending: { count: pending.length, memberDocIds: pending },
    used: { count: used.length, memberDocIds: used },
    scheduledDates,
  };
}

export function loadStatusSectionVisibility(): Record<CollectionStatusSection, boolean> {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_VISIBILITY };
  }

  try {
    const raw = window.localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_VISIBILITY };
    }
    const parsed = JSON.parse(raw) as Partial<Record<CollectionStatusSection, boolean>>;
    return {
      pending: parsed.pending ?? true,
      scheduled: parsed.scheduled ?? true,
      used: parsed.used ?? true,
    };
  } catch {
    return { ...DEFAULT_VISIBILITY };
  }
}

export function saveStatusSectionVisibility(
  visibility: Record<CollectionStatusSection, boolean>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
  } catch {
    // ignore quota / private mode
  }
}

export function toggleStatusSectionVisibility(
  section: CollectionStatusSection,
  current: Record<CollectionStatusSection, boolean>,
): Record<CollectionStatusSection, boolean> {
  const next = { ...current, [section]: !current[section] };
  saveStatusSectionVisibility(next);
  return next;
}

export function matchesCollectionMemberFilter(
  member: FirestoreCollectionMember,
  status: CollectionMemberStatus,
  scheduledDateIso?: string,
): boolean {
  if (member.status !== status) {
    return false;
  }
  if (status === 'scheduled' && scheduledDateIso) {
    return memberScheduledDateIso(member) === scheduledDateIso;
  }
  return true;
}
